import { $api } from "@/kibi/core/utils/api";
import type {
  PaginatedResponse,
  QueryOptions,
} from "@/kibi/core/utils/pagination_types";
import type { InjectionKey } from "vue";
import type {
  UpdateUserRequest,
  UserDTO,
  UserOrganizationDTO,
} from "../models/user_model";

export interface UserDataSource {
  getUsersByOrganization(orgId: string): Promise<UserDTO[]>;
  getSubordinateUsers(orgId: string): Promise<UserDTO[]>;
  getAllUsers(options: QueryOptions): Promise<PaginatedResponse<UserDTO[]>>;
  updateUser(id: string, request: UpdateUserRequest): Promise<UserDTO>;
  getUserDetails(id: string): Promise<UserDTO>;
  getUserOrganizations(id: string): Promise<UserOrganizationDTO[]>;
}

export const userDataSourceKey: InjectionKey<UserDataSource> =
  Symbol("UserDataSource");

export class UserDataSourceImpl implements UserDataSource {
  async getUsersByOrganization(orgId: string): Promise<UserDTO[]> {
    const response = await $api<{ data: UserDTO[] }>(
      `/accounts/organizations/${orgId}/users`,
      {
        method: "GET",
      },
    );
    return response.data;
  }

  async getSubordinateUsers(orgId: string): Promise<UserDTO[]> {
    const response = await $api<{ data: UserDTO[] }>(
      `/accounts/organizations/${orgId}/subordinate-users`,
      {
        method: "GET",
      },
    );
    return response.data;
  }

  async getAllUsers(
    options: QueryOptions,
  ): Promise<PaginatedResponse<UserDTO[]>> {
    const response = await $api<PaginatedResponse<UserDTO[]>>(
      "/accounts/users",
      {
        method: "GET",
        params: options,
      },
    );
    return response;
  }

  async updateUser(id: string, request: UpdateUserRequest): Promise<UserDTO> {
    const response = await $api<{ data: UserDTO }>(`/accounts/users/${id}`, {
      method: "PUT",
      body: request,
    });
    return response.data;
  }

  async getUserDetails(id: string): Promise<UserDTO> {
    const response = await $api<{ data: UserDTO }>(`/accounts/users/${id}`, {
      method: "GET",
    });
    return response.data;
  }

  async getUserOrganizations(id: string): Promise<UserOrganizationDTO[]> {
    const response = await $api<{ data: UserOrganizationDTO[] }>(
      `/accounts/${id}/organizations`,
      {
        method: "GET",
      },
    );
    return response.data;
  }
}
